import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Grid,
  Column,
  InlineLoading,
  InlineNotification,
  Tile,
} from '@carbon/react';
import { Download } from '@carbon/icons-react';
import './DownloadInformation.scss';

interface DownloadInformationProps {
  surveyId: string;
  formState: any; 
}

const DownloadInformation: React.FC<DownloadInformationProps> = ({ 
  surveyId, 
  formState // Destructure it here
}) => {
  const { t } = useTranslation();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<'none' | 'success' | 'error'>('none');
  const newBackendUrl = process.env.REACT_APP_NEW_BACKEND_URL;

  

  const handleGeneratePdf = async () => {
    setIsGeneratingPdf(true);
    setPdfStatus('none');
    
    try {
      // Create a payload with the current form state
      const payload = {
        surveyId: surveyId,
        surveyData: formState // This is your survey form state
      };
      
      // Send the data to the backend
      const pdfResponse = await fetch(`${newBackendUrl}/api/v1/surveys/${surveyId}/mmd`, {
        method: 'POST',
        headers: {
          'Accept': 'application/pdf',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (pdfResponse.ok) {
        // Handle the PDF - download it directly
        const pdfBlob = await pdfResponse.blob();
        
        // Create a download link for the PDF
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `mmd-${surveyId}.pdf`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setPdfStatus('success');
      } else {
        console.error('Failed to generate PDF:', pdfResponse.statusText);
        setPdfStatus('error');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      setPdfStatus('error');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <Grid>
      <Column sm={4} md={8} lg={16}>
        <h4>{t('survey.submission.download.title')}</h4>
        <p className="download-description">{t('survey.submission.download.description')}</p>
        
        <Tile className="download-tile">
          <h5>{t('survey.submission.download.reportTitle')}</h5>
          <p>{t('survey.submission.download.reportDescription')}</p>
          
          <div className="download-button-container">
            {isGeneratingPdf ? (
              <InlineLoading
                description={t('survey.submission.download.generating')}
                status="active"
              />
            ) : (
              <Button
                kind="tertiary"
                renderIcon={Download}
                onClick={handleGeneratePdf}
              >
                {t('survey.submission.download.generatePdf')}
              </Button>
            )}
          </div>
          
          {pdfStatus === 'success' && (
            <InlineNotification
              kind="success"
              title={t('survey.submission.download.success')}
              subtitle={t('survey.submission.download.successDescription')}
              hideCloseButton
              lowContrast
            />
          )}
          
          {pdfStatus === 'error' && (
            <InlineNotification
              kind="error"
              title={t('survey.submission.download.error')}
              subtitle={t('survey.submission.download.errorDescription')}
              hideCloseButton
              lowContrast
            />
          )}
        </Tile>
        
        <div className="download-note">
          <InlineNotification
            kind="info"
            title={t('survey.submission.download.noteTitle')}
            subtitle={t('survey.submission.download.noteDescription')}
            hideCloseButton
          />
        </div>
      </Column>
    </Grid>
  );
};

export default DownloadInformation;